import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, Type, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Maybe } from 'graphql/jsutils/Maybe';
import {
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  of,
  switchMap,
  take,
} from 'rxjs';
import { GraphqlModule } from 'src/app/graphql/graphql.module';
import { SharedService } from 'src/app/shared/shared.service';

import { BranchOfficeService } from '../../../services/branch-office.service';
import { BusinessUnitService } from '../../../services/business-unit.service';
import { EstablishmentService } from '../../../services/establishment.service';
import { BranchOfficeSimpleJson } from '../../../services/microservices/json/branch-office-simple.json';
import { BusinessUnitSimpleJson } from '../../../services/microservices/json/business-unit-simple.json';
import { EstablishmentSimpleJson } from '../../../services/microservices/json/establishment-simple.json';
import { ItemActiveJson } from '../../../services/microservices/json/item-active.json';
import { SignatureSimpleResponse } from '../../../services/microservices/response/signatureSimple.response';
import { BranchOfficeDialogComponent } from './dialogs/branch-office-dialog/branch-office-dialog.component';
import { BusinessUnitDialogComponent } from './dialogs/business-unit-dialog/business-unit-dialog.component';
import { EstablishmentDialogComponent } from './dialogs/establishment-dialog/establishment-dialog.component';

@Component({
  selector: 'app-establishment',
  templateUrl: './establishment.component.html',
  styleUrls: ['./establishment.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', overflow: 'hidden' })),
      state('expanded', style({ height: '*', overflow: 'auto' })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
    trigger('rotateArrow', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(90deg)' })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
  ],
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    CdkAccordionModule,
    MatExpansionModule,
    MatIconModule,
    FlexLayoutModule,
    MatButtonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    GraphqlModule,
  ],
})
export class EstablishmentComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  private sharedService = inject(SharedService);
  private establishmentService = inject(EstablishmentService);
  private branchOfficeService = inject(BranchOfficeService);
  private _snackBar = inject(MatSnackBar);
  private businessUnitService = inject(BusinessUnitService);
  private _unsubscribeAll!: Subject<any>;
  private signatureId!: number;

  isDisabledComponent = true;
  notification: any = {};
  txtBtn = '';
  establishments: Array<EstablishmentSimpleJson> = [];

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.load();
  }

  private load(): void {
    this.getTotalActivities();
  }

  private getTotalActivities(): void {
    this.sharedService.totalActivities$
      .pipe(
        filter(total => total > 0),
        distinctUntilChanged(),
        take(1),
      )
      .subscribe(total => {
        if (total > 0) {
          this.getSignatureId();
          this.isDisabledComponent = false;
        }
      });
  }

  private getSignatureId(): void {
    this.sharedService.signature$
      .pipe(
        take(1),
        distinctUntilChanged<SignatureSimpleResponse>(
          (prev, curr) => curr.id !== prev.id,
        ),
      )
      .subscribe(signature => {
        if (signature.id > 0) {
          this.signatureId = signature.id;
          this.getEstablishments(signature.id);
        }
      });
  }

  private async getEstablishments(signatureId: number) {
    this.establishments =
      await this.establishmentService.getBySignatureId(signatureId);
    this.getBusinessUnit();
  }

  private getBusinessUnit(): void {
    this.establishments.forEach(establishment => {
      establishment.branchesOffice.forEach(branchOffice => {
        this.businessUnitService
          .getBusinessUnitSummary(branchOffice.id)
          .subscribe(result => {
            if (result) {
              branchOffice.businessUnit = result;
            } else {
              this.openSnack(
                'Ocurrió un detalle en la lista de las unidades de negocio',
              );
            }
          });
      });
    });
  }

  editEstablishment(establishmentId: number, estIndex: number): void {
    const data = {
      id: establishmentId,
      signatureId: this.signatureId,
    };
    this.genericDialog(data, EstablishmentDialogComponent, {
      height: '355px',
      width: '350px',
    }).subscribe(establishment => {
      this.establishmentInArray(estIndex, establishment);
      if (establishmentId < 1 && establishment.id > 0) {
        const estIndex = this.establishments.findIndex(
          item => item.id === establishment.id,
        );
        this.editBranchOffice(
          -1,
          -1,
          establishment.id,
          establishment.number,
          estIndex,
        );
      }
    });
  }

  editBranchOffice(
    branchId: number,
    brnIndex: number,
    establishmentId: number,
    establishmentNumber: number,
    estIndex: number,
  ): void {
    const data = {
      id: branchId,
      establishmentId,
      establishmentNumber,
    };
    this.genericDialog(data, BranchOfficeDialogComponent, {
      height: '',
      width: '350px',
    }).subscribe(branchOffice => {
      this.branchInArray(estIndex, brnIndex, branchOffice);
      if (branchId < 1 && branchOffice.id > 0) {
        const brnIndex = this.establishments[estIndex].branchesOffice.findIndex(
          item => item.id === branchOffice.id,
        );
        this.editBussinesUnit(0, -1, branchOffice.id, brnIndex, estIndex);
      }
    });
  }

  editBussinesUnit(
    bussinesId: number | string,
    bssIndex: number,
    branchId: number,
    brnIndex: number,
    estIndex: number,
  ): void {
    const idsBusiness = {
      businessUnitId: Number(bussinesId),
      branchOfficeId: branchId,
    };
    this.genericDialog(idsBusiness, BusinessUnitDialogComponent, {
      width: '700px',
    }).subscribe(bussinesUnit => {
      this.refreshBussinesInArray(
        estIndex,
        brnIndex,
        branchId,
        bssIndex,
        bussinesUnit,
      );
    });
  }

  genericDialog(
    data: any,
    component: Type<any>,
    size: {
      height?: string;
      width: string;
    },
  ): Observable<any> {
    const dialogRef = this.dialog.open(component, {
      height: size.height,
      width: size.width,
      autoFocus: false,
      data,
    });
    return dialogRef.afterClosed().pipe(
      switchMap(result => {
        return of(result);
      }),
    );
  }

  switchBranch(
    estIndex: number,
    brnIndex: number,
    $event: MatSlideToggleChange,
  ): void {
    const branchOffice = this.establishments[estIndex].branchesOffice[brnIndex];
    const json: ItemActiveJson = {
      id: branchOffice.id,
      active: $event.checked,
    };
    this.branchOfficeService.updateActive(json).subscribe({
      next: () => {
        branchOffice.active = $event.checked;
      },
      error: () => {
        branchOffice.active = !$event.checked;
      },
    });
  }

  switchBusiness(
    estabIndex: number,
    branchIndex: number,
    busIndex: number,
    id: number | string,
    active: boolean,
  ): void {
    this.businessUnitService
      .updateBusinessUnitActive(Number(id), active)
      .subscribe(({ data, errors }) => {
        const branchOffice =
          this.establishments[estabIndex].branchesOffice[branchIndex];

        if (data && branchOffice.businessUnit) {
          const updatedBusinessUnit = JSON.parse(
            JSON.stringify(branchOffice.businessUnit),
          );

          if (updatedBusinessUnit[busIndex]) {
            updatedBusinessUnit[busIndex].active = active;
          }

          branchOffice.businessUnit = updatedBusinessUnit;
        }

        if (errors) {
          this.openSnack('No fue posible desactivar la unidad de negocio');
        }
      });
  }

  private establishmentInArray(
    estabIndex: number,
    estab: EstablishmentSimpleJson,
  ): void {
    if (estab && typeof estab.id !== 'undefined') {
      if (this.establishments) {
        if (estabIndex > -1) {
          this.establishments[estabIndex].tradeName = estab.tradeName;
        } else {
          this.establishments.push(estab);
        }
      } else {
        this.establishments = [estab];
      }
    }
  }

  private branchInArray(
    estabIndex: number,
    branchIndex: number,
    branchJson: BranchOfficeSimpleJson,
  ): void {
    if (branchJson && typeof branchJson.id !== 'undefined') {
      let array = this.establishments[estabIndex].branchesOffice;
      if (array) {
        if (branchIndex > -1) {
          array[branchIndex].citadel = branchJson.citadel;
        } else {
          array.push(branchJson);
        }
      } else {
        array = [branchJson];
      }
    }
  }

  private refreshBussinesInArray(
    estabIndex: number,
    branchIndex: number,
    branchId: number,
    busIndex: number,
    busJson: BusinessUnitSimpleJson,
  ): void {
    const branch = this.establishments[estabIndex].branchesOffice[branchIndex];
    this.businessUnitService
      .getBusinessUnitSummary(branchId)
      .pipe(take(2))
      .subscribe(result => {
        if (result) {
          branch.businessUnit = result;
        } else {
          this.openSnack(
            'Ocurrió un detalle al recargar las unidades de negocio',
          );
        }
      });
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  public addZero(nextNumber: number | Maybe<number>): string {
    if (!nextNumber) return '000';
    let number = '';
    if (nextNumber < 100) {
      number = '0';
    }
    if (nextNumber < 10) {
      number += '0';
    }
    number += nextNumber;
    return number;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }
}
